import { Collapse } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/static-intl";
import { useSearchParams } from "next/navigation";
import PlaceDetailsWindowForSearch from "./place-details-window-for-search";
import Iconify from "@/components/ui/Iconify";
import PlacesListFilters from "./places-list-filters";
import AreaList from "@/app/(authenticated)/(users)/places/_desktop/area-list";
import PlaceListSkeleton from "./place-list-skeleton";
import NoPlacePlaceholder from "./no-place-placeholder";
import InfiniteScroll from "react-infinite-scroll-component";
import PlaceTile from "./rf-place-tile";
import PlacesListSearchBox from "./places-list-search-box";
import ListEndMessage from "./place-list-end-msg";

const toCount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const formatCount = (value) => new Intl.NumberFormat("ko-KR").format(value || 0);

const getReviewCount = (place) =>
  Math.max(
    toCount(place?.totalReviews),
    toCount(place?.totalDbReviews),
    toCount(place?.numberOfVisitorReviews),
    toCount(place?.numberOfBlogReviews)
  );

const getHotpleReviewCount = (place) =>
  Math.max(toCount(place?.hotpleReviewCount), toCount(place?.hotpleRatingCount));

const getHotpleAverageRating = (place) => {
  const rating = Number(place?.hotpleAverageRating || 0);
  return Number.isFinite(rating) && rating > 0 ? rating : 0;
};

const formatRating = (value) => {
  const rating = Number(value || 0);
  if (!Number.isFinite(rating) || rating <= 0) return "";
  return rating.toFixed(1);
};

const getPlayCount = (place) =>
  Math.max(
    toCount(place?.hotpleStats?.videoCount),
    toCount(place?.hotpleStats?.effectiveVideoCount),
    toCount(place?.hotpleStats?.recentVideoCount)
  );

const isFiniteCoordinate = (coordinate) =>
  Number.isFinite(coordinate?.lat) && Number.isFinite(coordinate?.lng);

const isLngWithinBounds = (lng, west, east) => {
  if (west <= east) return lng >= west && lng <= east;
  return lng >= west || lng <= east;
};

const isCoordinateWithinBounds = (coordinate, bounds) => {
  if (!isFiniteCoordinate(coordinate) || !bounds) return false;

  const south = Number(bounds.south);
  const west = Number(bounds.west);
  const north = Number(bounds.north);
  const east = Number(bounds.east);

  if (
    !Number.isFinite(south) ||
    !Number.isFinite(west) ||
    !Number.isFinite(north) ||
    !Number.isFinite(east)
  ) {
    return false;
  }

  return (
    coordinate.lat >= south &&
    coordinate.lat <= north &&
    isLngWithinBounds(coordinate.lng, west, east)
  );
};

const HotpleLandingModal = ({ modalType, initialPlacesView, places, onClose }) => {
  const title = initialPlacesView?.landingTitle || initialPlacesView?.searchQuery || "핫플";
  const criteria = initialPlacesView?.criteria || [];
  const topPlaces = (places?.length ? places : initialPlacesView?.places || []).slice(0, 6);

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/35 px-3 py-5 md:items-center">
      <button
        aria-label="닫기"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        type="button"
      />
      <section
        aria-modal="true"
        className="relative w-full max-w-[420px] rounded-md border border-black/10 bg-white p-4 text-black shadow-xl dark:border-white/10 dark:bg-custom_black dark:text-white"
        role="dialog"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-black/60 dark:text-white/65">
              {modalType === "criteria" ? "선정 기준" : "리뷰와 반응"}
            </p>
            <h2 className="mt-1 text-lg font-bold leading-tight tracking-normal">
              {modalType === "criteria" ? `${title} 기준` : `${title} 신호`}
            </h2>
          </div>
          <button
            aria-label="닫기"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-black/10 text-black/55 hover:bg-black/5 dark:border-white/10 dark:text-white/65 dark:hover:bg-white/10"
            onClick={onClose}
            type="button"
          >
            <Iconify icon="mdi:close" />
          </button>
        </div>

        {modalType === "criteria" ? (
          <div className="mt-4 space-y-2">
            {criteria.map((item, index) => (
              <div
                className="flex gap-3 rounded-md border border-black/10 bg-black/[0.02] px-3 py-2 text-sm leading-relaxed dark:border-white/10 dark:bg-white/[0.04]"
                key={item}
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-black/70 text-xs font-bold text-white dark:bg-white/25">
                  {index + 1}
                </span>
                <span>{item}</span>
              </div>
            ))}
            <p className="pt-1 text-xs leading-relaxed text-black/55 dark:text-white/60">
              상위 장소는 공개 Play 소개, 업로더 다양성, 최근 후기 흐름을 함께 보며 비교합니다.
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {topPlaces.map((place, index) => (
              <div
                className="rounded-md border border-black/10 px-3 py-2 dark:border-white/10"
                key={place?.id || place?._id || place?.placeUniqueNumber || index}
              >
                <p className="truncate text-sm font-semibold">
                  {index + 1}. {place?.name}
                </p>
                <p className="mt-1 flex flex-wrap gap-1.5 text-[11px] font-semibold text-black/55 dark:text-white/60">
                  <span className="rounded border border-black/10 px-1.5 py-0.5 dark:border-white/10">
                    방문 리뷰 {formatCount(getReviewCount(place))}
                  </span>
                  <span className="rounded border border-black/10 bg-black/[0.02] px-1.5 py-0.5 text-black/60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/65">
                    {getHotpleReviewCount(place) > 0
                      ? `Hotple ${formatRating(getHotpleAverageRating(place))} · 평가 ${formatCount(getHotpleReviewCount(place))}`
                      : "Hotple 평가 가능"}
                  </span>
                  <span className="rounded border border-black/10 bg-black/[0.02] px-1.5 py-0.5 text-black/60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/65">
                    Play {formatCount(getPlayCount(place))}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const HotpleLandingPanel = ({ initialPlacesView, places }) => {
  const [modalType, setModalType] = useState(null);

  if (initialPlacesView?.rankingMode !== "play-tag-video-count") return null;

  const title = initialPlacesView.landingTitle || initialPlacesView.searchQuery || "핫플";
  const description =
    initialPlacesView.rankingDescription ||
    "공개 Play 영상 태그 수, 고유 업로더 수, 최근 반응을 기준으로 정렬합니다.";
  const relatedLinks = Array.isArray(initialPlacesView.relatedLinks)
    ? initialPlacesView.relatedLinks
    : [];
  const categoryChips = ["음식점", "카페", "디저트"];

  return (
    <>
      <section className="px-4 pt-3 pb-3 text-black dark:text-white">
        <h1 className="break-words text-[22px] font-bold leading-tight tracking-normal">
          {title}
        </h1>
        <p className="mt-1.5 text-xs leading-relaxed text-black/65 dark:text-white/65">
          {description}
        </p>

        <div className="mt-3 flex flex-nowrap gap-2 overflow-x-auto pb-1 text-[12px] font-semibold thin-scrollbar">
          {initialPlacesView.noSpaceKeyword ? (
            <span className="inline-flex shrink-0 items-center rounded-full border border-red/20 bg-red/5 px-3 py-1.5 text-red">
              {initialPlacesView.noSpaceKeyword}
            </span>
          ) : null}
          <button
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-black/10 px-3 py-1.5 text-black/70 hover:bg-black/[0.04] dark:border-white/10 dark:text-white/70 dark:hover:bg-white/[0.06]"
            onClick={() => setModalType("criteria")}
            type="button"
          >
            <Iconify icon="mdi:check-decagram-outline" />
            선정 기준
          </button>
          {categoryChips.map((chip) => (
            <span
              className="inline-flex shrink-0 items-center rounded-full border border-black/10 px-3 py-1.5 text-black/65 dark:border-white/10 dark:text-white/65"
              key={chip}
            >
              {chip}
            </span>
          ))}
          {relatedLinks.slice(0, 3).map((link) => (
            <a
              className="inline-flex shrink-0 items-center rounded-full border border-black/10 px-3 py-1.5 text-ink hover:bg-ink/[0.04] dark:border-white/10 dark:text-ink_on_dark dark:hover:bg-white/[0.06]"
              href={link.href}
              key={link.href}
            >
              {link.label}
            </a>
          ))}
        </div>
      </section>

      {modalType ? (
        <HotpleLandingModal
          initialPlacesView={initialPlacesView}
          modalType={modalType}
          onClose={() => setModalType(null)}
          places={places}
        />
      ) : null}
    </>
  );
};

const PlaceList = ({
  places,
  updatePageNo,
  userLocation,
  searchQuery,
  setSearchQuery,
  placeListLoader,
  // 지도가 동네를 보여주고 있을 때(확대 16.5 미만) 목록도 동네를 보여 준다.
  // 값은 지도가 이름표를 받을 때 같이 온 것이라 추가 조회가 없다.
  areaChips = null,
  areaLevel = null,
  onAreaSelect = null,
  addDistanceAttribute,
  completePlacesReset,
  totalPlaces,
  addQueryParam,
  removeQueryParam,
  mapCenter,
  mapBounds,
  setMapCenter,
  changeMapCenterAndZoomIn,
  setMapRedPinCenter,
  setPlaceSearchMapPinProperties,
  userFollowing,
  setUserFollowing,
  handlePostCardClick,
  isLeftSectionExpanded,
  placeFilters,
  setPlaceFilters,
  handlePlaceSorting,
  openPlaceAddModal,
  initialPlacesView,
}) => {
  const searchParams = useSearchParams();
  const t = useTranslations("places");

  const placeIdInList = searchParams.get("view-place-in-list");
  const viewPlaceId = searchParams.get("view-place");
  const isHotpleLanding =
    initialPlacesView?.rankingMode === "play-tag-video-count";

  // When a pin is selected the selected place floats to the TOP of the list; scroll
  // the list back to the top so the user always sees what they selected (instead of
  // it staying highlighted below the scroll fold).
  useEffect(() => {
    if (!viewPlaceId || typeof document === "undefined") return;
    const scrollContainer = document.getElementById("scrollableDiv");
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [viewPlaceId]);

  return (
    <Collapse
      in={isLeftSectionExpanded}
      orientation="horizontal"
      timeout={500}
      sx={{
        height: "100%",
        width: 400,
        minWidth: 400,
      }}
    >
      <div
        id="scrollableDiv"
        className="w-[400px] h-full flex flex-col overflow-x-hidden overflow-y-auto thin-scrollbar"
      >
        {placeIdInList ? (
          <PlaceDetailsWindowForSearch
            placeId={placeIdInList}
            handleReset={() => {
              removeQueryParam("view-place-in-list");
              completePlacesReset(true);
            }}
            mapCenter={mapCenter}
            setMapCenter={setMapCenter}
            userFollowing={userFollowing}
            setUserFollowing={setUserFollowing}
            handleContentModalOpen={handlePostCardClick}
            handleMapRoutesArrive={() => {}}
            handleMapRoutesDepart={() => {}}
            setMapRedPinCenter={setMapRedPinCenter}
            setPlaceSearchMapPinProperties={setPlaceSearchMapPinProperties}
            userLocation={userLocation}
            selectedLanguage="KOR"
          />
        ) : (
          <>
            <PlacesListSearchBox
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              userLocation={userLocation}
              mapCenter={mapCenter}
              addDistanceAttribute={addDistanceAttribute}
              completePlacesReset={completePlacesReset}
              addQueryParam={addQueryParam}
              removeQueryParam={removeQueryParam}
            />
            <PlacesListFilters
              placeFilters={placeFilters}
              setPlaceFilters={setPlaceFilters}
              handlePlaceSorting={handlePlaceSorting}
            />
            {searchQuery?.length > 0 &&
              searchQuery !== "My Location" &&
              !isHotpleLanding && (
              <div className="w-full flex items-center gap-1 px-2">
                <span className="text-xl font-semibold tracking-tight">
                  {searchQuery}
                </span>
                {!placeListLoader && (
                  <span className="text-red text-xl font-bold tracking-tight text-nowrap">
                    ({totalPlaces} {t("results")})
                  </span>
                )}
              </div>
            )}
            <HotpleLandingPanel
              initialPlacesView={initialPlacesView}
              places={places}
            />
            {areaLevel && !searchQuery ? (
              <AreaList
                areas={areaChips || []}
                level={areaLevel}
                onSelect={onAreaSelect}
              />
            ) : placeListLoader ? (
              <PlaceListSkeleton />
            ) : (
              <>
                {places?.length === 0 ? (
                  <NoPlacePlaceholder openPlaceAddModal={openPlaceAddModal} />
                ) : (
                  <InfiniteScroll
                    dataLength={places?.length}
                    next={updatePageNo}
                    hasMore={
                      places?.length < 100 && places?.length < totalPlaces
                    }
                    scrollThreshold={0.75}
                    scrollableTarget="scrollableDiv"
                    loader={
                      <div className="pt-6 pb-20 lg:pb-8 text-black/40 dark:text-white/40 text-xs flex items-center justify-center gap-1">
                        <Iconify
                          className={"!text-inherit"}
                          icon={"eos-icons:loading"}
                        />
                        {t("loading more places")}
                      </div>
                    }
                    endMessage={
                      <ListEndMessage openPlaceAddModal={openPlaceAddModal} />
                    }
                  >
                    {places?.map((place, i) => (
                      <PlaceTile
                        i={i}
                        key={i}
                        place={place}
                        isHotpleLanding={isHotpleLanding}
                        setPlaces={() => {}}
                        handlePlaceListTileClick={() => {
                          const nextMapCenter = {
                            lat: Number(place?.latitude),
                            lng: Number(place?.longitude),
                          };
                          const isPlaceVisible = isCoordinateWithinBounds(
                            nextMapCenter,
                            mapBounds
                          );

                          if (isHotpleLanding) {
                            if (changeMapCenterAndZoomIn) {
                              changeMapCenterAndZoomIn(nextMapCenter, {
                                jump: true,
                                zoom: 19,
                              });
                            } else {
                              setMapCenter(nextMapCenter);
                            }
                            return;
                          }

                          if (changeMapCenterAndZoomIn && !isPlaceVisible) {
                            changeMapCenterAndZoomIn(nextMapCenter, {
                              jump: true,
                              zoom: 17,
                            });
                          } else if (!changeMapCenterAndZoomIn && !isPlaceVisible) {
                            setMapCenter(nextMapCenter);
                          }
                        }}
                      />
                    ))}
                  </InfiniteScroll>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Collapse>
  );
};

export default PlaceList;
